import { useEffect, useRef, useState } from 'react';

import {
  ProfileOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { App, Modal, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { TiendaPerfectaStopDetail } from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import { useRouteReportTheme } from '../hooks/useRouteReportTheme';
import { STOP_STATE_TAG_COLOR, getStopUiState } from '../types';
import {
  countAnsweredSteps,
  getStopDetailUiState,
  getStopMaterialTotals,
  hasStopMaterials,
  isIncidentActivityDone,
  isPopActivityDone,
  isSameStartAndFinish,
  usedIncidentForBypass,
} from '../utils/stopDetail';
import { formatDate, formatTime, getDurationMinutes } from '../utils/time';
import StopActivityModal, { StopActivityKey } from './StopActivityModal';
import StopDetailSkeleton, { StopDetailHint } from './StopDetailSkeleton';
import StopMap from './StopMap';

const I18N_BASE = 'scenes.route_report.components.stop_detail';

interface StopMetricProps {
  label: string;
  value: string | null;
  hint?: string | null;
  modifier?: string;
}

function StopMetric({ label, value, hint, modifier }: StopMetricProps) {
  return (
    <div className={['stop-metric', modifier].filter(Boolean).join(' ')}>
      <div className="stop-metric__label">{label}</div>
      <div
        className={[
          'stop-metric__value',
          value ? '' : 'stop-metric__value--empty',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value || '—'}
      </div>
      {/* El relleno es un espacio DURO (U+00A0), no uno común: un espacio común
          se colapsa y no genera línea, así que la tarjeta sin subtítulo quedaría
          más baja que las otras tres. El `min-height` del SCSS cubre lo mismo
          por si alguien lo reemplaza por un espacio normal. */}
      <div className="stop-metric__sub">{hint || ' '}</div>
    </div>
  );
}

interface StopActivityCardProps {
  name: string;
  meta: string;
  state: { label: string; color?: string };
  done: boolean;
  onOpen: () => void;
}

/**
 * Una actividad de la parada.
 *
 * Una actividad que no se realizó sigue en la lista, deshabilitada: su ausencia
 * también es información —la parada podía requerirla— y esconderla haría que la
 * lista cambiara de forma entre una parada y otra.
 */
function StopActivityCard({
  name,
  meta,
  state,
  done,
  onOpen,
}: StopActivityCardProps) {
  return (
    <button
      type="button"
      className={['stop-activity-card', done ? '' : 'stop-activity-card--off']
        .filter(Boolean)
        .join(' ')}
      disabled={!done}
      onClick={onOpen}
    >
      <span className="stop-activity-card__icon">
        <ProfileOutlined />
      </span>
      <span className="stop-activity-card__body">
        <span className="stop-activity-card__name">{name}</span>
        <span className="stop-activity-card__meta">{meta}</span>
      </span>
      <Tag color={state.color}>{state.label}</Tag>
      {done && (
        <span className="stop-activity-card__caret">
          <RightOutlined aria-hidden="true" />
        </span>
      )}
    </button>
  );
}

interface StopDetailModalProps {
  stopId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  userTimeZone: string;
  /** Lo que la fila de la grilla ya sabe, para que el bloque de carga acierte. */
  hint?: StopDetailHint;
}

/**
 * El detalle de una parada.
 *
 * La primera pantalla contesta QUÉ pasó: cuándo entró, cuándo salió, con qué
 * camioneta, dónde, y qué actividades se realizaron. Los pasos del formulario y
 * los materiales NO viven acá: son el contenido de una actividad, y una
 * actividad se abre en su propio modal.
 */
function StopDetailModal({
  stopId,
  isOpen,
  onClose,
  userTimeZone,
  hint,
}: StopDetailModalProps) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const themeVars = useRouteReportTheme();

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<TiendaPerfectaStopDetail | null>(null);
  const [openActivity, setOpenActivity] = useState<StopActivityKey | null>(
    null
  );

  /**
   * El detalle vale sólo si es el de la parada que se está pidiendo.
   *
   * Al cerrar no se limpia —para que el contenido no se derrumbe durante la
   * animación de salida— así que al abrir OTRA parada el detalle anterior sigue
   * en el estado. Sin este filtro le ganaba al `hint` nuevo y el encabezado
   * anunciaba el local equivocado mientras cargaba: peor que un bloque de
   * carga, porque afirma una identidad falsa en vez de no afirmar ninguna.
   */
  const currentDetail =
    detail && stopId && detail.stop.id === stopId ? detail : null;

  /* `onClose` y el traductor cambian de identidad en cada render del padre y no
     tienen por qué volver a disparar la petición. Se leen desde una ref para
     poder dejarlos fuera de las dependencias sin quedarse con una versión
     vieja.
     La ref se escribe en un efecto y no durante el render: mutar algo externo
     mientras se renderiza deja la ref adelantada respecto del árbol que se
     terminó montando si React descarta ese render. */
  const latest = useRef({ onClose, message, t });

  useEffect(() => {
    latest.current = { onClose, message, t };
  });

  useEffect(() => {
    /* Al CERRAR no se toca nada. antd desmonta el contenido recién cuando
       termina la animación de salida, así que limpiar el detalle acá devolvía
       la vista al bloque de carga y encogía el encabezado durante todo el
       fundido: exactamente el salto que el bloque de carga existe para
       evitar. */
    if (!isOpen || !stopId) return;

    setOpenActivity(null);

    let cancelled = false;

    const fetchStop = async () => {
      setLoading(true);

      /* Se pide cada vez que se abre en lugar de cachear el detalle: las URLs
         de las fotos se firman por petición y caducan en una hora, así que una
         respuesta guardada sirve imágenes rotas. */
      const response = await TiendaPerfectaService.getStop(stopId);

      if (cancelled) return;

      const payload = response.data as TiendaPerfectaStopDetail;

      /* Un `200` sin la forma del contrato es una respuesta rota, no un
         detalle vacío: sin este chequeo `setDetail(undefined)` dejaba el
         bloque de carga girando para siempre, sin mensaje y sin salida. Es la
         misma guarda que ya usa `useRouteReport` sobre esta API.

         Se valida también que sea la parada PEDIDA. El render descarta un
         detalle cuyo id no coincida, así que sin esta comprobación una
         respuesta con otro id volvería a dejar el bloque de carga puesto para
         siempre: el mismo callejón sin salida, por otra puerta. */
      if (response.success && payload?.stop?.id === stopId) {
        setDetail(payload);
      } else {
        const error = response.data as ApiErrorData;
        latest.current.message.error(
          (error?.message as string) ||
            latest.current.t(`${I18N_BASE}.errors.fetch_stop`),
          3.5
        );
        latest.current.onClose();
      }

      setLoading(false);
    };

    fetchStop();

    return () => {
      cancelled = true;
      /* No se toca `loading` acá. Al reabrir, el efecto vuelve a ponerlo en
         `true` antes de pedir, así que no queda atascado; bajarlo en la
         limpieza, en cambio, destapaba el detalle viejo en mitad de la
         animación de salida cuando se cerraba con una petición en vuelo. */
    };
  }, [isOpen, stopId]);

  /**
   * El encabezado no espera a la petición.
   *
   * Todo lo que muestra —la secuencia, el nombre del local, su código, la ruta,
   * el distribuidor y el estado— ya estaba en la fila desde la que se abrió el
   * modal, así que se pinta con el `hint` y el detalle sólo lo confirma. Sin
   * esto el título arranca como una línea de texto genérico y crece 28px al
   * llegar la respuesta, justo cuando el usuario está leyendo.
   *
   * El estado sale de `getStopUiState` cuando viene del hint y de
   * `getStopDetailUiState` cuando viene del detalle: son la misma regla sobre
   * dos formas del mismo dato —el `bypass` de la grilla es el `usedForBypass`
   * del detalle— así que el tag no cambia al llegar la respuesta.
   */
  const renderTitle = () => {
    const head = currentDetail
      ? {
          sequence: currentDetail.stop.sequence,
          name: currentDetail.stop.label || currentDetail.stop.code,
          code: currentDetail.stop.code,
          routeCode: currentDetail.route.code,
          distributorName: currentDetail.distributor?.tradeName ?? null,
          state: getStopDetailUiState(currentDetail),
        }
      : hint
      ? {
          sequence: hint.sequence,
          name: hint.label || hint.code,
          code: hint.code,
          routeCode: hint.routeCode,
          distributorName: hint.distributorName,
          state: getStopUiState(hint.status, hint.hasBypass),
        }
      : null;

    /* Sin hint y sin detalle no hay nada que afirmar todavía: pasa sólo si el
       modal se abre desde otro lugar que no sea la grilla. */
    if (!head) return t(`${I18N_BASE}.title`);

    return (
      <div>
        <div className="stop-detail__title">
          <span className="stop-detail__sequence">{head.sequence}</span>
          <span className="stop-detail__name">{head.name}</span>
          <Tag color={STOP_STATE_TAG_COLOR[head.state]}>
            {t(`scenes.route_report.stop_states.${head.state}`)}
          </Tag>
        </div>
        <div className="stop-detail__crumbs">
          <span className="route-report-num">{head.code}</span>
          <i aria-hidden="true">·</i>
          <span className="route-report-num">{head.routeCode}</span>
          <i aria-hidden="true">·</i>
          {head.distributorName ? (
            <span>{head.distributorName}</span>
          ) : (
            /* `distributor` puede ser null y la parada sigue siendo
               alcanzable: se abrió antes de que hubiera uno asignado. */
            <span className="stop-detail__muted">
              {t(`${I18N_BASE}.no_distributor`)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderMetrics = (stop: TiendaPerfectaStopDetail) => {
    const minutes = getDurationMinutes(
      stop.stop.startedAt,
      stop.stop.finishedAt
    );

    /* Tres formas, no dos: una duración de horas exactas escrita con el formato
       general sale "1h 0min", que nadie escribe así. */
    const duration =
      minutes === null
        ? null
        : minutes < 60
        ? t(`${I18N_BASE}.duration_minutes`, { minutes })
        : minutes % 60 === 0
        ? t(`${I18N_BASE}.duration_exact_hours`, { hours: minutes / 60 })
        : t(`${I18N_BASE}.duration_hours`, {
            hours: Math.floor(minutes / 60),
            minutes: minutes % 60,
          });

    return (
      <div className="stop-detail__metrics">
        <StopMetric
          label={t(`${I18N_BASE}.metrics.start`)}
          value={formatTime(stop.stop.startedAt, userTimeZone)}
          hint={formatDate(stop.stop.startedAt, userTimeZone)}
        />
        <StopMetric
          label={t(`${I18N_BASE}.metrics.end`)}
          value={formatTime(stop.stop.finishedAt, userTimeZone)}
          hint={
            stop.stop.finishedAt
              ? formatDate(stop.stop.finishedAt, userTimeZone)
              : stop.stop.startedAt
              ? t(`${I18N_BASE}.hints.still_in_store`)
              : null
          }
        />
        <StopMetric
          label={t(`${I18N_BASE}.metrics.duration`)}
          value={duration}
          hint={duration ? t(`${I18N_BASE}.hints.time_in_store`) : null}
        />
        {/* `name` es el nombre operativo de la camioneta y `plate` su patente:
            dos campos distintos del contrato, y los dos se muestran para poder
            identificar la unidad por cualquiera de ellos. */}
        <StopMetric
          label={t(`${I18N_BASE}.metrics.team`)}
          value={stop.van?.name ?? null}
          hint={stop.van?.plate ?? null}
          modifier="stop-metric--team"
        />
      </div>
    );
  };

  const renderActivities = (stop: TiendaPerfectaStopDetail) => {
    const steps = stop.form.steps;
    /* Los materiales entran en la cuenta: el paso que los contiene no guarda su
       respuesta en `answer`, así que sin esto una parada ejecutada por completo
       nunca llegaría a "Completada". */
    const answered = countAnsweredSteps(steps, stop.materials);
    const popDone = isPopActivityDone(stop);
    const totals = getStopMaterialTotals(stop.materials);

    /* El resumen de materiales sólo se anuncia si la actividad se puede abrir:
       prometerlos en una card deshabilitada manda al usuario a una pantalla que
       no puede ver. Pasa en una parada pendiente, que publica sus materiales
       sugeridos sin haberse ejecutado.

       Ya no exige un paso `materials` en la plantilla: el modal de la actividad
       muestra lo entregado igual, en su propia sección, cuando la plantilla no
       lo declara. */
    const showsMaterials = popDone && hasStopMaterials(stop.materials);

    const popMeta = !steps.length
      ? /* Una plantilla nombrada sin pasos no es "sin plantilla": el contrato
           publica `templateName` por separado, así que se dice cuál es. */
        stop.form.templateName ?? t(`${I18N_BASE}.activity_meta.no_template`)
      : !answered && !showsMaterials
      ? t(`${I18N_BASE}.activity_meta.waiting_answers`, {
          count: steps.length,
        })
      : [
          t(`${I18N_BASE}.activity_meta.answered`, {
            answered,
            count: steps.length,
          }),
          showsMaterials
            ? /* Los dos totales se enumeran por separado: "8 de 11" sonaría a
                 meta incumplida, y lo sugerido no es una meta. */
              t(`${I18N_BASE}.activity_meta.materials`, {
                suggested: totals.suggested,
                delivered: totals.delivered,
              })
            : null,
        ]
          .filter(Boolean)
          .join(' · ');

    const popState = !popDone
      ? { label: t(`${I18N_BASE}.activity_states.not_done`) }
      : answered === steps.length
      ? {
          label: t(`${I18N_BASE}.activity_states.completed`),
          color: 'cyan',
        }
      : { label: t(`${I18N_BASE}.activity_states.incomplete`), color: 'gold' };

    const incident = stop.incident;
    const incidentDone = isIncidentActivityDone(stop);

    /* El texto del vacío depende del estado de la parada: el contrato manda
       `incident: null` en los tres casos y significan cosas distintas. */
    const emptyIncidentMeta =
      stop.stop.status === 'finished'
        ? t(`${I18N_BASE}.activity_meta.no_incident_finished`)
        : stop.stop.status === 'in_progress'
        ? t(`${I18N_BASE}.activity_meta.no_incident_in_progress`)
        : t(`${I18N_BASE}.activity_meta.no_incident_pending`);

    const incidentState = !incident
      ? { label: t(`${I18N_BASE}.activity_states.not_done`) }
      : incident.usedForBypass
      ? { label: t(`${I18N_BASE}.activity_states.closed_stop`), color: 'gold' }
      : { label: t(`${I18N_BASE}.activity_states.registered`), color: 'blue' };

    return (
      <div className="stop-section">
        <div className="stop-section__title">
          {t(`${I18N_BASE}.activities_title`)}
        </div>

        <StopActivityCard
          name={t(`${I18N_BASE}.activities.pop`)}
          meta={popMeta}
          state={popState}
          done={popDone}
          onOpen={() => setOpenActivity('pop')}
        />

        <StopActivityCard
          name={t(`${I18N_BASE}.activities.incident`)}
          meta={
            incident
              ? `${incident.reason} · ${incident.code}`
              : emptyIncidentMeta
          }
          state={incidentState}
          done={incidentDone}
          onOpen={() => setOpenActivity('incident')}
        />
      </div>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        width={680}
        /* La clase de carga apaga el scroll del cuerpo: una silueta no tiene
           nada que leer, así que una barra de desplazamiento sobre ella sólo
           agrega ruido. */
        className={[
          'stop-detail-modal',
          loading || !currentDetail ? 'stop-detail-modal--loading' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        /* antd portea el modal a `document.body`, fuera del div de la vista
           donde se publican las variables de marca, así que hay que volver a
           publicarlas acá. Sin esto el modal se pinta con el color de reserva
           del SCSS y sale de otro color que la pantalla de atrás en cualquier
           compañía cuyo primario no sea justo ese. */
        styles={{ content: themeVars }}
        title={renderTitle()}
      >
        {loading || !currentDetail ? (
          <StopDetailSkeleton hint={hint} />
        ) : (
          <div className="stop-detail">
            {renderMetrics(currentDetail)}

            {/* Sólo cuando la incidencia se usó efectivamente para cerrar la
                parada: una registrada que no la cerró no explica el resultado y
                vive en su card de actividad. Va antes del mapa porque contesta
                la primera pregunta de quien abre una parada en amarillo. */}
            {usedIncidentForBypass(currentDetail) && currentDetail.incident && (
              <div className="stop-section">
                <div className="stop-alert">
                  <WarningOutlined
                    className="stop-alert__icon"
                    aria-hidden="true"
                  />
                  <span className="stop-alert__body">
                    <span className="stop-alert__line">
                      <b>{t(`${I18N_BASE}.bypass_reason`)}</b>{' '}
                      {currentDetail.incident.reason}
                    </span>
                    {currentDetail.incident.note && (
                      <span className="stop-alert__note">
                        {currentDetail.incident.note}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="stop-section">
              <div className="stop-section__title">
                {t(`${I18N_BASE}.location_title`)}
                {/* La misma regla que usa el mapa para dibujar un solo pin:
                    sale de `isSameStartAndFinish` y no de una comparación
                    repetida acá, para que no puedan discrepar. */}
                {isSameStartAndFinish(currentDetail) && (
                  <span className="stop-section__count">
                    {t(`${I18N_BASE}.same_point`)}
                  </span>
                )}
              </div>
              <StopMap detail={currentDetail} />
            </div>

            {renderActivities(currentDetail)}
          </div>
        )}
      </Modal>

      <StopActivityModal
        activity={openActivity}
        detail={currentDetail}
        userTimeZone={userTimeZone}
        onClose={() => setOpenActivity(null)}
      />
    </>
  );
}

export default StopDetailModal;
